import { useCallback, useEffect, useRef, useState } from 'react';
import { attachmentUploadError, postAttachment } from './attachmentUpload';

// Owned by the upload panel, which stays mounted when its drawer closes.
export default function useAttachmentUpload({ endpoint, fields, messageApi, onSaved }) {
  const [pending, setPending] = useState(0);
  const options = useRef();
  options.current = { endpoint, fields, messageApi, onSaved };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);
  useEffect(() => {
    if (!pending) return undefined;
    const warnBeforeLeaving = event => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', warnBeforeLeaving);
    return () => window.removeEventListener('beforeunload', warnBeforeLeaving);
  }, [pending]);

  const handleUpload = useCallback(async ({ file, onSuccess, onError, onProgress }) => {
    const snapshot = options.current;
    setPending(count => count + 1);
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(snapshot.fields).forEach(([name, value]) => formData.append(name, value));
    try {
      const response = await postAttachment(snapshot.endpoint, formData, {
        onUploadProgress: event => {
          if (event.total) onProgress?.({ percent: Math.min(99, Math.round(event.loaded * 100 / event.total)) });
        },
      });
      onSuccess?.(response.data);
      snapshot.messageApi.success(`${file.name} uploaded successfully`);
      // A completed upload for a previous HBL/mail type must not replace the current list.
      if (mounted.current && JSON.stringify(snapshot.fields) === JSON.stringify(options.current.fields)) {
        try {
          await options.current.onSaved?.();
        } catch (_) {
          snapshot.messageApi.warning(`${file.name} was saved, but the list could not be refreshed. Reopen the panel to reload it.`);
        }
      }
    } catch (error) {
      const failure = new Error(attachmentUploadError(error));
      snapshot.messageApi.error(`${file.name}: ${failure.message}`, 8);
      onError?.(failure);
    } finally {
      if (mounted.current) setPending(count => count - 1);
    }
  }, []);

  return { uploading: pending > 0, handleUpload };
}
